import { IResourceListProps } from "../common/ra-types";
import { DefaultProLogsList } from "../common/list/DefaultProLogsList";
import { Datagrid, FunctionField, Record, TextField } from "react-admin";
import { Box, useTheme } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { ReportDto, ReportStatus } from "../../common/data/service-proxies";
import { ProLogsEmpty } from "../common/list/ProLogsEmpty";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import resourceNames from "../../common/data/resourceNames";
import { REPORT_DISPLAY_NAMES } from "../../constants/ReportMacineNames";
import ReportDetails from "./ReportDetails";
import ProLogsDateField from "../common/data-grid-fields/ProLogsDateField";

interface ReportsArchiveProps extends IResourceListProps {
  embedded?: boolean;
  filter?: { [key: string]: any };
  targetTenantName?: string | null;
}

export const ReportsArchive = ({
  embedded = false,
  resource = resourceNames.REPORTS_ARCHIVE,
  targetTenantName,
  ...props
}: ReportsArchiveProps) => {
  const theme = useTheme();
  return (
    <div className={embedded ? undefined : "content-container"}>
      <DefaultProLogsList
        empty={<ProLogsEmpty iconType={AssignmentIndOutlinedIcon} />}
        title={embedded ? "View ELD usage by events" : "Archive"}
        className="content-list"
        bulkActionButtons={false}
        actions={false}
        canExport={false}
        syncWithLocation={!embedded}
        sort={{ field: "requestedTime", order: "DESC" }}
        basePath={`/${resource}`}
        resource={resource}
        {...props}
      >
        <Datagrid>
          <FunctionField<ReportDto & Record>
            label="Report"
            render={(record) => {
              if (!record?.reportName) return "-";
              return REPORT_DISPLAY_NAMES[
                record.reportName as keyof typeof REPORT_DISPLAY_NAMES
              ];
            }}
          />
          <ProLogsDateField
            showTime
            source="requestedTime"
            label="Time requested"
          />
          <TextField source="reportType" label="File Type" />
          <FunctionField<ReportDto & Record>
            source="reportInput"
            label="Input"
            render={(record) => {
              return (
                <ReportDetails
                  machineName={record?.reportName}
                  reportInput={record?.reportInput}
                  reportType={record?.reportType}
                  targetTenantName={targetTenantName}
                />
              );
            }}
          />
          <TextField source="requestedByUser" label="Requested By" />
          <FunctionField<ReportDto & Record>
            source="status"
            label="Status"
            render={(record) => {
              if (!record?.status) return "-";
              const status = record.status as ReportStatus;
              return (
                <p
                  style={{
                    margin: 0,
                    borderRadius: 4,
                    padding: 4,
                    textAlign: "center",
                    maxWidth: "fit-content",
                    minWidth: 65,
                    backgroundColor:
                      status === ReportStatus.Successful
                        ? theme.palette.success.main
                        : status === ReportStatus.Failed
                        ? theme.palette.error.main
                        : theme.palette.warning.main,
                    color:
                      status === ReportStatus.Successful
                        ? theme.palette.success.contrastText
                        : status === ReportStatus.Failed
                        ? theme.palette.error.contrastText
                        : theme.palette.warning.contrastText,
                  }}
                >
                  {status}
                </p>
              );
            }}
          />
          <FunctionField<ReportDto & Record>
            label="Download"
            render={(record) => (
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {record?.fileLink ? (
                  <GetAppIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = record.fileLink || "";
                      link.download = "";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  />
                ) : (
                  "-"
                )}
              </Box>
            )}
          />
        </Datagrid>
      </DefaultProLogsList>
    </div>
  );
};
